import { useState, useCallback } from 'react'

export interface GeocodeResult {
  latitude: number
  longitude: number
  displayName: string
}

export interface GeocodeError {
  message: string
  code: 'NOT_FOUND' | 'RATE_LIMITED' | 'NETWORK_ERROR' | 'INVALID_ADDRESS'
}

interface UseGeocodeReturn {
  geocode: (address: string) => Promise<GeocodeResult | null>
  isLoading: boolean
  error: GeocodeError | null
  clearError: () => void
}

// Nominatim API - free geocoding from OpenStreetMap
// Usage policy: https://operations.osmfoundation.org/policies/nominatim/
// - Max 1 request per second
// - Provide a valid User-Agent
// - Cache results where possible
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search'

export function useGeocode(): UseGeocodeReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<GeocodeError | null>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const geocode = useCallback(async (address: string): Promise<GeocodeResult | null> => {
    if (!address.trim()) {
      setError({ message: 'Please provide an address', code: 'INVALID_ADDRESS' })
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        q: address,
        format: 'json',
        limit: '1',
        addressdetails: '1',
      })

      const response = await fetch(`${NOMINATIM_URL}?${params}`, {
        headers: {
          'User-Agent': 'DEAI-Web-Venue-Manager/1.0',
        },
      })

      if (response.status === 429) {
        setError({
          message: 'Too many requests. Please wait a moment and try again.',
          code: 'RATE_LIMITED'
        })
        return null
      }

      if (!response.ok) {
        setError({
          message: 'Failed to lookup address. Please try again.',
          code: 'NETWORK_ERROR'
        })
        return null
      }

      const data = await response.json()

      if (!data || data.length === 0) {
        setError({
          message: 'Address not found. Please check and try again.',
          code: 'NOT_FOUND'
        })
        return null
      }

      const result = data[0]
      return {
        latitude: parseFloat(result.lat),
        longitude: parseFloat(result.lon),
        displayName: result.display_name,
      }
    } catch (err) {
      setError({
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      })
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    geocode,
    isLoading,
    error,
    clearError,
  }
}

// Helper to build a full address string from venue address parts
export function buildAddressString(address: {
  street?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
}): string {
  const parts = [
    address.street,
    address.city,
    address.state,
    address.postalCode,
    address.country,
  ].filter(Boolean)

  return parts.join(', ')
}
